import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
   return twMerge(clsx(inputs));
}

/**
 * formatMobileNumber - Formats a mobile number (default Indian format)
 * @param {string|number} mobile
 * @param {string} country
 * @returns {string}
 */
export function formatMobileNumber(mobile, country = "IN") {
   if (!mobile) return "";

   const cleaned = String(mobile).replace(/\D/g, "");

   switch (country.toUpperCase()) {
      case "IN":
         if (cleaned.length === 10) return `+91 ${cleaned}`;
         if (cleaned.startsWith("91") && cleaned.length === 12) {
            return `+${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
         }
         return `+91 ${cleaned}`;

      default:
         return cleaned;
   }
}


// ===== Global Locale Config =====
export const LOCALE_CONFIG = {
   locale: "en-IN",
   currency: "INR",
   dateOptions: {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
   },
   datetimeOptions: {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
   },
   timeOptions: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
   },
   numberOptions: {
      maximumFractionDigits: 2
   }
};


export const statusFilterFn = (
   row,
   columnId,
   filterValue
) => {
   if (!filterValue?.length) return true
   const status = row.getValue(columnId)
   return filterValue.includes(status);
}


// Custom filter function for multi-column searching
export const multiColumnFilterFn = (row, columnId, filterValue) => {
   const searchableRowContent =
      `${row.original.name} ${row.original.email}`.toLowerCase()
   const searchTerm = (filterValue ?? "").toLowerCase()
   return searchableRowContent.includes(searchTerm);
}




export function dateFormat(dateString, showFull = false) {
   if (!dateString) return "";
   try {
      const date = new Date(dateString);
      return date.toLocaleString(LOCALE_CONFIG.locale, showFull ? LOCALE_CONFIG.datetimeOptions: LOCALE_CONFIG.dateOptions).toUpperCase();
   } catch (e) {
      console.error("Invalid date string:", dateString, e);
      return dateString;
   }
}

/**
 * matchCurrentUrl - Checks if the current browser URL matches any given paths
 * @param {string[]} paths - Array of paths or patterns to match
 * @returns {boolean} - true if any path matches current URL
 */
export function matchCurrentUrl(paths = []) {
   if (typeof window === 'undefined') return false;

   const currentPath = window.location.pathname;

   return paths.some((path) => {
      // Exact match
      if (path === currentPath) return true;

      // Wildcard match, e.g. "/dashboard/*"
      if (path.endsWith('*')) {
         const base = path.replace('*', '');
         return currentPath.startsWith(base);
      }

      // Regex pattern support
      try {
         const regex = new RegExp(path);
         return regex.test(currentPath);
      } catch {
         return false;
      }
   });
}

/**
 * getUserInitials - Returns the initials of a given full name
 * @param {string} name - Full name of the user
 * @returns {string} - Uppercase initials (e.g., "AJ" for "Akshay Jumbade")
 */
export function getUserInitials(name = "") {
   if (!name) return "";

   return name
      .trim()
      .split(/\s+/)
      .map(word => word[0]?.toUpperCase() || "")
      .join("");
}

/**
 * detectValueType - Detects the type of a given value
 * @param {*} value - The value to check
 * @returns {string} - Detected type (e.g., 'email', 'phone', 'url', etc.)
 */
export function detectValueType(value) {
   if (value === null || value === undefined) return 'unknown';

   if (Array.isArray(value)) {
      if (value.every(v => typeof v === 'string')) return 'array:strings';
      if (value.every(v => typeof v === 'object' && !Array.isArray(v))) return 'array:objects';
      return 'array:mixed';
   }

   if (typeof value === 'object') return 'object';

   if (typeof value === 'number') {
      return Number.isInteger(value) ? 'number' : 'float';
   }

   if (typeof value !== 'string') return typeof value;

   const text = value.trim();
   if (text.length > 150) return 'longtext';

   // URL with protocol
   if (/^[a-zA-Z]+:\/\/[^\s]+$/.test(text)) {
      if (/^[a-zA-Z]+:\/\/$/.test(text.split(':')[0] + '://')) return 'app-url';
      return 'url';
   }

   // Email
   if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) return 'email';

   // Phone number
   if (/^\+?\d{7,15}$/.test(text)) return 'phone';

   // Date / datetime
   if (!isNaN(Date.parse(text))) {
      const date = new Date(text);
      if (text.match(/T\d{2}:\d{2}/) || text.match(/\d{2}:\d{2}/)) return 'datetime';
      return 'date';
   }

   // Number or float in string form
   if (/^-?\d+$/.test(text)) return 'number';
   if (/^-?\d*\.\d+$/.test(text)) return 'float';

   // Username (alphanumeric with underscores or dots)
   if (/^[a-zA-Z0-9_.-]{3,}$/.test(text)) return 'username';

   // Gender detection
   if (['male', 'female', 'other'].includes(text.toLowerCase())) return 'gender';

return 'text';
}

// ===== Formatter Functions =====

/**
 * _format - Formats a value based on its detected type
 * @param {*} value
 * @returns {string}
 */
export function _format(value) {
   const type = detectValueType(value);
   const { locale, currency, dateOptions, timeOptions, numberOptions } = LOCALE_CONFIG;

   // Helper: check if given date is same as today
   const isSameDay = (date) => {
      const now = new Date();
      return (
         date.getFullYear() === now.getFullYear() &&
         date.getMonth() === now.getMonth() &&
         date.getDate() === now.getDate()
      );
   };

   // Helper: get relative time string
   const getRelativeTime = (date) => {
      const now = new Date();
      const diffMs = date - now;
      const diffMins = Math.round(diffMs / (1000 * 60));
      const diffHours = Math.round(diffMins / 60);

      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

      if (Math.abs(diffMins) < 60) return rtf.format(diffMins, 'minute');
      if (Math.abs(diffHours) < 24) return rtf.format(diffHours, 'hour');

      return new Intl.DateTimeFormat(locale, { ...dateOptions, ...timeOptions }).format(date);
   };

   switch (type) {
      case 'date':
         return dateFormat(value);

      case 'datetime': {
         const date = new Date(value);
         return isSameDay(date)
            ? getRelativeTime(date)
            : dateFormat(value, true);
      }

      case 'number':
         return new Intl.NumberFormat(locale, numberOptions).format(Number(value));

      case 'float':
         return new Intl.NumberFormat(locale, { ...numberOptions, minimumFractionDigits: 2 }).format(parseFloat(value));

      case 'currency':
      case 'money':
         return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(value));

      case 'email':
      case 'url':
      case 'app-url':
      case 'phone':
      case 'username':
      case 'gender':
      case 'text':
      case 'longtext':
         return value;

      case 'array:strings':
         return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(value);

      case 'array:objects':
         return JSON.stringify(value, null, 2);

      case 'object':
         return JSON.stringify(value, null, 2);

      default:
         return String(value);
   }
}


